import type { Spec, TextMark } from "vega";
import type { BaseChartSpecOptions } from "../types.js";
import { formatExpression, type NumberFormat } from "./format.js";

// Vega-Lite has no polar line, so a radar is written in full Vega: the
// categories are spokes around a centre, each one's value a point along its
// spoke, joined into a closed polygon. Every mark is placed from the centre
// and a radius that leaves room for the labels, so the chart fits whatever
// size it is given without autosizing.

export interface RadarChartSpecOptions extends BaseChartSpecOptions {
  /** Field naming each spoke. */
  category: string;
  /** Field for each spoke's value. */
  value: string;
  /** Names for the tooltip. */
  categoryTitle?: string;
  valueTitle?: string;
  /** The rings' shape: polygons through the spokes, or circles. */
  grid?: "polygon" | "circle";
  /** Fill the polygon. */
  filled?: boolean;
  /** The series color. Unset: the theme's first categorical color. */
  color?: string;
  /** Name each spoke at its end. */
  labels?: boolean;
  /** How values read in the tooltip. */
  valueFormat?: NumberFormat;
}

/** The rings drawn, as fractions of the radius. */
const RINGS = [0.25, 0.5, 0.75, 1];
/** Segments a circular ring is drawn with. */
const CIRCLE_SEGMENTS = 72;
/** Room left around the radar for its spoke labels. */
const LABEL_ROOM = 28;

/** Each spoke's angle, clockwise from the top. */
function spokeAngle(index: number, count: number): number {
  return -Math.PI / 2 + (2 * Math.PI * index) / Math.max(1, count);
}

/** The rings as straight segments: through the spokes, or round. */
function ringSegments(spokes: number, grid: "polygon" | "circle") {
  const segments = grid === "circle" ? CIRCLE_SEGMENTS : spokes;
  if (segments < 2) return [];
  return RINGS.flatMap((ring) =>
    Array.from({ length: segments }, (_, index) => ({
      ring,
      a1: spokeAngle(index, segments),
      a2: spokeAngle(index + 1, segments),
    })),
  );
}

const x = (radius: string, angle: string) => `cx + ${radius} * cos(${angle})`;
const y = (radius: string, angle: string) => `cy + ${radius} * sin(${angle})`;
const POINT_X = x("scale('radial', datum.value)", "datum.angle");
const POINT_Y = y("scale('radial', datum.value)", "datum.angle");

function tooltipSignal(
  categoryTitle: string,
  valueTitle: string,
  valueFormat: NumberFormat | undefined,
): string {
  return `{${JSON.stringify(categoryTitle)}: datum.key, ${JSON.stringify(valueTitle)}: ${formatExpression("datum.value", valueFormat)}}`;
}

/** The marks' color: the given one, else the theme's first category color. */
function seriesColor(color: string | undefined) {
  return color ? { value: color } : { scale: "color", value: "series" };
}

function labelMarks(labels: boolean): TextMark[] {
  if (!labels) return [];
  const at = "(radius + 8)";
  return [
    {
      type: "text",
      from: { data: "table" },
      encode: {
        update: {
          x: { signal: x(at, "datum.angle") },
          y: { signal: y(at, "datum.angle") },
          text: { field: "key" },
          align: [
            { test: "abs(cos(datum.angle)) < 0.2", value: "center" },
            { test: "cos(datum.angle) < 0", value: "right" },
            { value: "left" },
          ],
          baseline: [
            { test: "sin(datum.angle) > 0.2", value: "top" },
            { test: "sin(datum.angle) < -0.2", value: "bottom" },
            { value: "middle" },
          ],
          limit: { signal: "max(24, width / 2 - radius - 12)" },
        },
      },
    },
  ];
}

/** Builds a Vega radar chart spec. */
export function radarChartSpec(options: RadarChartSpecOptions): Spec {
  const {
    data,
    category,
    value,
    categoryTitle = category,
    valueTitle = value,
    grid = "polygon",
    filled = false,
    color,
    labels = true,
    valueFormat,
    title,
    subtitle,
  } = options;

  const values = data.map((row, index) => {
    const number = Number(row[value]);
    return {
      key: String(row[category] ?? ""),
      value: Number.isFinite(number) ? number : 0,
      angle: spokeAngle(index, data.length),
    };
  });
  const titleValue = subtitle ? { text: title ?? "", subtitle } : title;
  const fill = seriesColor(color);
  const tooltip = {
    signal: tooltipSignal(categoryTitle, valueTitle, valueFormat),
  };

  return {
    signals: [
      { name: "cx", update: "width / 2" },
      { name: "cy", update: "height / 2" },
      {
        name: "radius",
        update: `max(0, min(width, height) / 2 - ${labels ? LABEL_ROOM : 6})`,
      },
    ],
    data: [
      { name: "table", values },
      { name: "rings", values: ringSegments(values.length, grid) },
    ],
    scales: [
      {
        name: "radial",
        type: "linear",
        domain: { data: "table", field: "value" },
        domainMin: 0,
        zero: true,
        nice: false,
        range: [0, { signal: "radius" }],
      },
      {
        name: "color",
        type: "ordinal",
        domain: ["series"],
        range: "category",
      },
    ],
    marks: [
      {
        type: "rule",
        from: { data: "rings" },
        encode: {
          update: {
            x: { signal: x("radius * datum.ring", "datum.a1") },
            y: { signal: y("radius * datum.ring", "datum.a1") },
            x2: { signal: x("radius * datum.ring", "datum.a2") },
            y2: { signal: y("radius * datum.ring", "datum.a2") },
          },
        },
      },
      {
        type: "rule",
        from: { data: "table" },
        encode: {
          update: {
            x: { signal: "cx" },
            y: { signal: "cy" },
            x2: { signal: x("radius", "datum.angle") },
            y2: { signal: y("radius", "datum.angle") },
          },
        },
      },
      {
        type: "line",
        from: { data: "table" },
        encode: {
          update: {
            interpolate: { value: "linear-closed" },
            x: { signal: POINT_X },
            y: { signal: POINT_Y },
            stroke: fill,
            strokeWidth: { value: 2 },
            fill,
            fillOpacity: { value: filled ? 0.4 : 0 },
          },
        },
      },
      {
        type: "symbol",
        from: { data: "table" },
        encode: {
          update: {
            x: { signal: POINT_X },
            y: { signal: POINT_Y },
            size: { value: 36 },
            fill,
            tooltip,
          },
        },
      },
      ...labelMarks(labels),
    ],
    ...(titleValue !== undefined ? { title: titleValue } : {}),
  };
}
